import { observer } from 'mobx-react';

import { ShoppingCartItemData, ShoppingCartItemGroupData } from '../../../app/data/model';
import SecondaryButton from '../../../app/shared/components/Button/SecondaryButton';
import Modal from '../../../app/shared/components/Modal/Modal';
import HeaderLevel3 from '../../../app/shared/components/Typography/HeaderLevel3';
import { TextLabelSmall } from '../../../app/shared/components/Typography/TextLabels';
import { QuantityInfoTranslation } from '../../localization/SiteTranslation';
import { CartActionType } from '../ConfiguratorActionsState';
import { AddedToCartItem } from './AddedToCartItem/AddedToCartItem';
import { AddedToCartItemGroup } from './AddedToCartItemGroup/AddedToCartItemGroup';
import css from './AddedToCartModal.css';
import { AddedToCartModalState } from './AddedToCartModalState';

interface AddedToCartModalProps {
  actionType: CartActionType;
  state: AddedToCartModalState<ShoppingCartItemData | ShoppingCartItemGroupData>;
}

export const AddedToCartModal = observer(({ state, actionType }: AddedToCartModalProps) => {
  const translation = state.translation;

  const quantityUpdateOnly =
    state.recentlyAddedItem &&
    state.recentlyAddedQuantity &&
    state.recentlyAddedItem.quantity !== state.recentlyAddedQuantity;
  const headerText =
    quantityUpdateOnly || actionType === 'Update' ? translation.updatedInCartHeader : translation.addedToCartHeader;

  return (
    <Modal
      classes={{
        base: css.AddedToCartModal,
        afterOpen: css.modalAfterOpen,
        beforeClose: css.modalBeforeClose,
      }}
      contentClassName={css.modalContent}
      modal={state}
      translation={{ closeButton: '' }}
    >
      <HeaderLevel3 className={css.modalHeader}>
        <div>
          <>
            {headerText}{' '}
            {quantityUpdateOnly && (
              <QuantityInfo quantityUpdate={state.recentlyAddedQuantity} translation={translation.quantityInfo} />
            )}
          </>
        </div>
      </HeaderLevel3>
      {state.recentlyAddedItem && Object.hasOwnProperty.call(state.recentlyAddedItem, 'code') && (
        <AddedToCartItem
          item={state.recentlyAddedItem as ShoppingCartItemData}
          translation={translation.addedToCartItem}
        />
      )}
      {state.recentlyAddedItem && Object.hasOwnProperty.call(state.recentlyAddedItem, 'modularCode') && (
        <AddedToCartItemGroup
          item={state.recentlyAddedItem as ShoppingCartItemGroupData}
          translation={translation.addedToCartItem}
        />
      )}
      <div className={css.shoppingCartDetailsGoToCartButtonContainer}>
        <SecondaryButton className={css.shoppingCartDetailsGoToCartButton} onClick={state.goToCart}>
          {translation.goToCartButton}
        </SecondaryButton>
      </div>
    </Modal>
  );
});

const QuantityInfo = observer(
  ({
    quantityUpdate: quantityAdded,
    translation,
  }: {
    quantityUpdate: number;
    translation: QuantityInfoTranslation;
  }) => {
    const labelText =
      quantityAdded === 1
        ? translation.productAddedText
        : translation.productsAddedText.interpolate([['quantityAdded', quantityAdded.toString()]]);
    return (
      <TextLabelSmall className={css.quantityInfo}>
        <>{labelText}</>
      </TextLabelSmall>
    );
  },
);
